import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Pencil,
  Check,
  Plus,
  X,
  ChevronDown,
  Mic,
  Paperclip,
  Send,
} from "lucide-react";
import AttributePicker from "./attribute-picker";
import { useSegmentAttributes, useSegmentCounts, useSegmentEvents } from "./api";
import {
  ALL_OPERATORS,
  AttributeCatalog,
  AttributeCatalogField,
  AttributeSelectOption,
  ContactScope,
  EventCatalog,
  EventCatalogItem,
  EventRule,
  OPERATOR_LABELS,
  RuleOperator,
  RuleTree,
  Segment,
  SegmentContactRow,
  SegmentCountsResponse,
  SegmentRule,
  SegmentRuleGroup,
  VALUELESS_OPERATORS,
  eventItemKey,
  groupsToRuleTree,
  isChannelGroupedEvents,
  isRuleFilled,
  maskEmail,
  maskPhone,
  ruleTreeToGroups,
} from "./types";
import {  UserGroupIcon } from "../../../assets/icon";

// A Broadcast Events rule (e.g. "Broadcast Delivered", "Broadcast Clicked")
// can be narrowed to one channel via EventRule.attributePath ===
// "broadcast_channel" — the data model and rule_tree serialization for this
// already existed (see types.ts's EventRule/ruleToTreeNode), it just had no
// UI. "Custom Events" don't carry a channel, so this only applies to entries
// that actually appear under the "Broadcast Events" catalog group.
const isBroadcastEventType = (eventType: string, events: EventCatalog): boolean => {
  const group = events["Broadcast Events"];
  if (!group) return false;
  const items = isChannelGroupedEvents(group) ? Object.values(group).flat() : group;
  return items.some((item) => eventItemKey(item) === eventType);
};

// The ConvoAI "sparkle face" icon used in the panel header and its floating
// reopen button — replaces the generic lucide Sparkles icon.
const ConvoAiIcon: React.FC<{ size?: number; className?: string; color?: string }> = ({ size = 21, className, color = "#4B4141" }) => (
  <svg
    width={size}
    height={(size * 23) / 21}
    viewBox="0 0 21 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 1C10.901 1 11.774 1.12 12.605 1.344C12.0859 1.61661 11.658 2.03539 11.3743 2.54855C11.0906 3.06172 10.9635 3.64678 11.0087 4.2314C11.0539 4.81602 11.2695 5.37458 11.6287 5.83802C11.9879 6.30145 12.4751 6.64946 13.03 6.839L13.408 6.968C13.5528 7.01735 13.6844 7.09926 13.7926 7.20745C13.9007 7.31563 13.9827 7.44719 14.032 7.592L14.162 7.97C14.3519 8.52433 14.6999 9.01093 15.1631 9.36972C15.6264 9.72852 16.1845 9.9438 16.7687 9.989C17.3529 10.0342 17.9376 9.90734 18.4505 9.62407C18.9634 9.3408 19.3821 8.91353 19.655 8.395C19.8841 9.24438 20.0001 10.1203 20 11C20 16.523 15.523 21 10 21C8.11001 21 6.34001 20.475 4.83201 19.563L1.80001 20.454C1.62593 20.5052 1.44126 20.5086 1.26542 20.4638C1.08959 20.4189 0.929079 20.3276 0.800767 20.1992C0.672456 20.0709 0.581082 19.9104 0.536252 19.7346C0.491423 19.5587 0.494793 19.3741 0.546008 19.2L1.43801 16.168C0.495062 14.6093 -0.00231244 12.8218 8.08285e-06 11C8.08285e-06 5.477 4.47701 1 10 1ZM6.50001 9.5C6.10218 9.5 5.72065 9.65804 5.43935 9.93934C5.15804 10.2206 5.00001 10.6022 5.00001 11C5.00001 11.3978 5.15804 11.7794 5.43935 12.0607C5.72065 12.342 6.10218 12.5 6.50001 12.5C6.89783 12.5 7.27936 12.342 7.56067 12.0607C7.84197 11.7794 8.00001 11.3978 8.00001 11C8.00001 10.6022 7.84197 10.2206 7.56067 9.93934C7.27936 9.65804 6.89783 9.5 6.50001 9.5ZM13.5 9.5C13.1022 9.5 12.7207 9.65804 12.4393 9.93934C12.158 10.2206 12 10.6022 12 11C12 11.3978 12.158 11.7794 12.4393 12.0607C12.7207 12.342 13.1022 12.5 13.5 12.5C13.8978 12.5 14.2794 12.342 14.5607 12.0607C14.842 11.7794 15 11.3978 15 11C15 10.6022 14.842 10.2206 14.5607 9.93934C14.2794 9.65804 13.8978 9.5 13.5 9.5ZM17 0C17.2086 8.32272e-05 17.4119 0.0653942 17.5816 0.18679C17.7512 0.308185 17.8786 0.479587 17.946 0.677L18.076 1.055C18.376 1.934 19.066 2.625 19.946 2.925L20.323 3.054C20.5202 3.12157 20.6914 3.24908 20.8125 3.41869C20.9337 3.58831 20.9988 3.79155 20.9988 4C20.9988 4.20845 20.9337 4.41169 20.8125 4.58131C20.6914 4.75092 20.5202 4.87843 20.323 4.946L19.945 5.076C19.066 5.376 18.375 6.066 18.075 6.946L17.946 7.323C17.8784 7.5202 17.7509 7.69134 17.5813 7.81252C17.4117 7.93369 17.2085 7.99883 17 7.99883C16.7916 7.99883 16.5883 7.93369 16.4187 7.81252C16.2491 7.69134 16.1216 7.5202 16.054 7.323L15.924 6.945C15.7759 6.51114 15.5303 6.11699 15.2062 5.79283C14.882 5.46866 14.4879 5.2231 14.054 5.075L13.677 4.946C13.4798 4.87843 13.3087 4.75092 13.1875 4.58131C13.0663 4.41169 13.0012 4.20845 13.0012 4C13.0012 3.79155 13.0663 3.58831 13.1875 3.41869C13.3087 3.24908 13.4798 3.12157 13.677 3.054L14.055 2.924C14.934 2.624 15.625 1.934 15.925 1.054L16.054 0.677C16.1214 0.479587 16.2488 0.308185 16.4184 0.18679C16.5881 0.0653942 16.7914 8.32272e-05 17 0Z"
      fill={color}
    />
    <path
      d="M6.51172 14.8539C6.51172 14.8539 7.45199 17.0078 10.075 17.0078C12.698 17.0078 13.5885 14.832 13.5885 14.832"
      stroke={color === "#4B4141" ? "white" : "#4B4141"}
      strokeLinecap="round"
    />
  </svg>
);

const createEmptyRule = (): SegmentRule => ({
  id: `rule_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  kind: "attribute",
  field: "",
  operator: "",
  value: "",
});

const createEmptyGroup = (): SegmentRuleGroup => ({
  id: `group_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  rules: [createEmptyRule()],
});

interface SegmentBuilderProps {
  segment: Segment;
  onBack: () => void;
  onSave: (ruleTree: RuleTree, scope: ContactScope, name: string) => void;
  saving?: boolean;
}

const SegmentBuilder: React.FC<SegmentBuilderProps> = ({ segment, onBack, onSave, saving }) => {
  const [name, setName] = useState(segment.name);
  const [editingName, setEditingName] = useState(false);
  const [scope, setScope] = useState<ContactScope>(segment.sourceScope);
  const [openPickerFor, setOpenPickerFor] = useState<{ groupId: string; ruleId: string } | null>(null);

  const [convoAiOpen, setConvoAiOpen] = useState(true);
  const [convoPrompt, setConvoPrompt] = useState("");

  // ── Hydrate once from whatever GET /segments/{id} already gave us (index.tsx
  // fetches full detail before opening the builder) — avoids re-fetching
  // anything that's already in hand.
  const [initial] = useState(() => {
    const hydratedGroups = ruleTreeToGroups(segment.ruleTree);
    return { groups: hydratedGroups.length > 0 ? hydratedGroups : [createEmptyGroup()], hasRules: hydratedGroups.length > 0 };
  });
  const [groups, setGroups] = useState<SegmentRuleGroup[]>(initial.groups);

  // ── Live catalogs (GET /segments/attributes, GET /segments/events) ──────
  const { makeRequest: getAttributeCatalog } = useSegmentAttributes();
  const { makeRequest: getEventCatalog } = useSegmentEvents();
  const [attributeCatalog, setAttributeCatalog] = useState<AttributeCatalog>({});
  const [attributesLoading, setAttributesLoading] = useState(false);
  // Grouped: { "Broadcast Events": { sms: [...], whatsapp: [...], ... }, "Custom Events": [...] }.
  const [events, setEvents] = useState<EventCatalog>({});
  const [eventsLoading, setEventsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setAttributesLoading(true);
      try {
        const [response, status] = await getAttributeCatalog(undefined, { source_scope: scope });
        if (status >= 200 && status < 300) {
          const data = response?.data;
          setAttributeCatalog(data && typeof data === "object" ? data : {});
        }
      } finally {
        setAttributesLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(() => {
    (async () => {
      setEventsLoading(true);
      try {
        // Same source_scope the attribute catalog fetch above already sends
        // — switching between "All Connected Data" and "ConvoCRM Data"
        // should scope the events list the same way it scopes attributes.
        const [response, status] = await getEventCatalog(undefined, { source_scope: scope });
        if (status >= 200 && status < 300) {
          const data = response?.data;
          if (data && typeof data === "object") {
            // "broadcast_filters" is a sibling array of extra narrowing
            // fields (not yet wired into the rule-editing UI), not an event
            // category — keep it out of the group map so it doesn't render
            // as a bogus "broadcast_filters" chip full of the wrong shape.
            const { broadcast_filters, ...catalog } = data as Record<string, unknown>;
            setEvents(catalog as EventCatalog);
          } else {
            setEvents({});
          }
        }
      } finally {
        setEventsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // ── Baseline scope totals (GET /segments/counts, no rule_tree) — drives the
  // "X total available contacts" figure under the scope cards.
  const { makeRequest: getScopeCounts } = useSegmentCounts();
  const [scopeCounts, setScopeCounts] = useState<SegmentCountsResponse | null>(null);
  useEffect(() => {
    (async () => {
      const [response, status] = await getScopeCounts(undefined, { source_scope: scope });
      if (status >= 200 && status < 300) {
        const data = response?.data;
        if (data?.[scope]) setScopeCounts(data);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);
  const baselineTotal = scopeCounts?.[scope]?.contacts ?? null;

  // ── Live matching count ──────────────────────────────────────────────
  // GET /segments/counts?rule_tree=<encoded current tree> — a read-only
  // preview, so nothing gets persisted just from editing (only the explicit
  // "Save Segment" button below calls PUT /segments/{id}/rules). Reuses the
  // same hook as the baseline scope totals above.
  const [matchingCount, setMatchingCount] = useState<number | null>(
    initial.hasRules ? segment.validContacts + segment.invalidContacts : null,
  );
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState(false);
  // Whichever rule field the user last touched — so the small loading spinner
  // during the debounced count call appears next to that specific field
  // instead of only in the ConvoAI panel.
  const [lastEditedRuleId, setLastEditedRuleId] = useState<string | null>(null);

  // The segment's own saved contacts table — only ever reflects what's
  // actually persisted (populated on open; refreshed by re-opening after a
  // save), never a live/unsaved preview, since GET /segments/counts doesn't
  // return contact rows and PUT-ing on every keystroke would silently save
  // half-built drafts.
  const [contactRows] = useState<SegmentContactRow[]>(initial.hasRules ? segment.contactRows || [] : []);

  useEffect(() => {
    // Deliberately fires on mount too (not just on later edits) — opening an
    // existing segment hydrates `groups` from its saved rule_tree, and that
    // needs its own live /segments/counts?rule_tree=... call right away,
    // not just the scope-wide baseline fetched above.
    const hasFilledRule = groups.some((g) => g.rules.some(isRuleFilled));
    if (!hasFilledRule) {
      setMatchingCount(null);
      setCountError(false);
      return;
    }
    // 3s debounce (not the usual ~500ms) — Eben 2026-07-29: the matching
    // count keeps changing as more rules/attributes get added, so this
    // waits for a genuine pause before spending a request on it.
    const timeout = setTimeout(async () => {
      setCountLoading(true);
      try {
        const ruleTree = groupsToRuleTree(groups);
        const [response, status] = await getScopeCounts(undefined, { rule_tree: JSON.stringify(ruleTree), source_scope: scope });
        if (status < 200 || status >= 300) {
          setCountError(true);
          return;
        }
        const data: SegmentCountsResponse | undefined = response?.data;
        const scopeCount = data?.[scope];
        if (scopeCount && typeof scopeCount.contacts === "number") {
          setMatchingCount(scopeCount.contacts);
          setCountError(false);
        } else {
          setCountError(true);
        }
      } catch {
        setCountError(true);
      } finally {
        setCountLoading(false);
      }
    }, 3000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, scope]);

  // ── Rule group helpers ──────────────────────────────────────────────
  const updateRule = (groupId: string, ruleId: string, updater: (rule: SegmentRule) => SegmentRule) => {
    setLastEditedRuleId(ruleId);
    setGroups((prev) =>
      prev.map((g) => (g.id !== groupId ? g : { ...g, rules: g.rules.map((r) => (r.id === ruleId ? updater(r) : r)) })),
    );
  };

  const removeRule = (groupId: string, ruleId: string) => {
    setGroups((prev) =>
      prev
        .map((g) => (g.id !== groupId ? g : { ...g, rules: g.rules.filter((r) => r.id !== ruleId) }))
        .filter((g) => g.rules.length > 0),
    );
  };

  const addRuleToGroup = (groupId: string) => {
    setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, rules: [...g.rules, createEmptyRule()] } : g)));
  };

  const addGroup = () => setGroups((prev) => [...prev, createEmptyGroup()]);
  const clearRules = () => setGroups([createEmptyGroup()]);

  const handleSelectAttribute = (groupId: string, ruleId: string, field: AttributeCatalogField) => {
    updateRule(groupId, ruleId, (r) => ({
      id: r.id,
      kind: "attribute",
      field: field.field,
      label: field.label,
      type: field.type,
      operators: field.operators,
      options: field.options,
      operator: field.operators[0] || "equals",
      value: "",
    }));
    setOpenPickerFor(null);
  };

  const handleSelectEvent = (groupId: string, ruleId: string, event: EventCatalogItem) => {
    updateRule(groupId, ruleId, (r) => ({
      id: r.id,
      kind: "event",
      eventType: eventItemKey(event),
    }));
    setOpenPickerFor(null);
  };

  // Narrows a Broadcast Events rule (Delivered/Clicked/etc.) to one channel —
  // "" clears the narrowing back to "any channel". Reuses the exact
  // attribute_path/operator/value shape the backend already round-trips
  // (see types.ts's EventRule/ruleToTreeNode), so this also just works for
  // rule_trees that arrive pre-filled — e.g. the campaign report's "Add to
  // Segment" draft (see index.tsx's pendingSegmentDraft handoff).
  const handleChangeChannelFilter = (groupId: string, ruleId: string, channel: string) => {
    updateRule(groupId, ruleId, (r) =>
      r.kind !== "event"
        ? r
        : channel
        ? { ...r, attributePath: "broadcast_channel", eventOperator: "equals", eventValue: channel }
        : { ...r, attributePath: undefined, eventOperator: "", eventValue: undefined, eventValueTo: undefined },
    );
  };

  // Real channel keys from the live catalog (GET /segments/events groups
  // "Broadcast Events" by channel) — falls back to the known fixed set only
  // if that hasn't loaded yet.
  const broadcastEventsGroup = events["Broadcast Events"];
  const channelOptions =
    broadcastEventsGroup && isChannelGroupedEvents(broadcastEventsGroup)
      ? Object.keys(broadcastEventsGroup)
      : ["sms", "whatsapp", "email", "rcs"];

  const handleSave = () => {
    if (!name.trim()) {
      toast.error("Give this segment a name first.");
      return;
    }
    onSave(groupsToRuleTree(groups), scope, name.trim());
  };

  const handleAskConvoAi = () => {
    if (!convoPrompt.trim()) return;
    toast.info("ConvoAI isn't connected yet — this is a UI preview.");
    setConvoPrompt("");
  };

  // The per-field spinner only has somewhere to render while its own rule
  // row still exists — if the edit that triggered recalculation was removing
  // that row (or its whole group) entirely, fall back to this indicator so
  // "something is being calculated" is still visible.
  const fieldLoaderStillVisible = groups.some((g) => g.rules.some((r) => r.id === lastEditedRuleId));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm mb-4">
        <button
          onClick={onBack}
          className="p-1 rounded-lg text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent border-none cursor-pointer"
        >
          <ArrowLeft size={16} />
        </button>
        <button onClick={onBack} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 bg-transparent border-none cursor-pointer p-0">
          All Segments
        </button>
        <span className="text-gray-300 dark:text-gray-700">/</span>
        <span className="font-medium text-gray-800 dark:text-gray-200">Create New Segment</span>
      </div>

      <div className="flex gap-6 items-start flex-wrap xl:flex-nowrap">
        {/* Main column */}
        <div className="flex-1 min-w-0 w-full space-y-5">
          {/* Segment name */}
          <div className="flex items-center gap-2">
            {editingName ? (
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => e.key === "Enter" && setEditingName(false)}
                className="text-xl font-semibold text-gray-900 dark:text-gray-100 border-b border-indigo-300 dark:border-indigo-500 outline-none bg-transparent"
              />
            ) : (
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{name}</h1>
            )}
            <button
              onClick={() => setEditingName(true)}
              className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 bg-transparent border-none cursor-pointer"
            >
              <Pencil size={15} />
            </button>
          </div>

          {/* Contact scope */}
          <div>
            <p className="text-sm text-gray-500 dark:text-white mb-2">Choose the contacts you want to include in this segment</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ScopeCard
                selected={scope === "all"}
                title="All Connected Data"
                description="Includes contacts from all sources connected to ConvoHub"
                onClick={() => setScope("all")}
              />
              <ScopeCard
                selected={scope === "crm"}
                title="ConvoCRM Data"
                description="Includes only contacts from your CRM"
                onClick={() => setScope("crm")}
              />
            </div>
          </div>

          {/* Rule builder */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
            <p className="text-sm text-gray-500 dark:text-white mb-3">Choose contact attributes and conditions for this segment</p>

            <div className="space-y-3">
              {groups.map((group, groupIndex) => (
                <React.Fragment key={group.id}>
                  <div className="bg-indigo-50/40 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/30 rounded-xl p-3">
                    {group.rules.map((rule, ruleIndex) => (
                      <React.Fragment key={rule.id}>
                        <RuleRow
                          rule={rule}
                          showLoader={countLoading && lastEditedRuleId === rule.id}
                          isPickerOpen={openPickerFor?.groupId === group.id && openPickerFor?.ruleId === rule.id}
                          onOpenPicker={() => setOpenPickerFor({ groupId: group.id, ruleId: rule.id })}
                          onClosePicker={() => setOpenPickerFor(null)}
                          attributeCatalog={attributeCatalog}
                          attributesLoading={attributesLoading}
                          events={events}
                          eventsLoading={eventsLoading}
                          onSelectAttribute={(field) => handleSelectAttribute(group.id, rule.id, field)}
                          onSelectEvent={(event) => handleSelectEvent(group.id, rule.id, event)}
                          onChangeOperator={(operator) =>
                            updateRule(group.id, rule.id, (r) => (r.kind === "attribute" ? { ...r, operator, value: "", valueTo: undefined } : r))
                          }
                          onChangeValue={(value) =>
                            updateRule(group.id, rule.id, (r) => (r.kind === "attribute" ? { ...r, value } : r))
                          }
                          onChangeValueTo={(valueTo) =>
                            updateRule(group.id, rule.id, (r) => (r.kind === "attribute" ? { ...r, valueTo } : r))
                          }
                          onChangeOccurredInDays={(days) =>
                            updateRule(group.id, rule.id, (r) => (r.kind === "event" ? { ...r, occurredInDays: days } : r))
                          }
                          onChangeCountOperator={(countOperator) =>
                            updateRule(group.id, rule.id, (r) =>
                              r.kind === "event" ? { ...r, countOperator, countValue: countOperator ? r.countValue : undefined } : r,
                            )
                          }
                          onChangeCountValue={(countValue) =>
                            updateRule(group.id, rule.id, (r) => (r.kind === "event" ? { ...r, countValue } : r))
                          }
                          channelOptions={channelOptions}
                          onChangeChannelFilter={(channel) => handleChangeChannelFilter(group.id, rule.id, channel)}
                          onRemove={() => removeRule(group.id, rule.id)}
                        />
                        {ruleIndex < group.rules.length - 1 && (
                          <div className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 my-1.5">AND</div>
                        )}
                      </React.Fragment>
                    ))}
                    <button
                      onClick={() => addRuleToGroup(group.id)}
                      className="mt-2 flex items-center gap-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 transition-colors"
                    >
                      <Plus size={13} /> Add new rule
                    </button>
                  </div>
                  {groupIndex < groups.length - 1 && <div className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">OR</div>}
                </React.Fragment>
              ))}
            </div>

            <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={addGroup}
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 transition-colors"
                >
                  OR <Plus size={13} />
                </button>
                {countLoading && !fieldLoaderStillVisible && <FieldLoader />}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={clearRules} className="text-sm font-medium text-gray-500 dark:text-white hover:text-gray-700 dark:hover:text-gray-200 px-4 py-2 bg-transparent border-none cursor-pointer">
                  Clear
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-5 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving..." : "Save Segment"}
                </button>
              </div>
            </div>
          </div>

          {/* Live matching count — GET /segments/counts?rule_tree=..., read-only preview. */}
          <div className="rounded-xl ">
            {countLoading ? (
              <p className="text-sm text-gray-500 dark:text-white py-2">Calculating matching contacts…</p>
            ) : countError ? (
              <p className="text-sm text-gray-500 dark:text-white py-2">Couldn't calculate matching contacts right now.</p>
            ) : matchingCount !== null ? (
              <p className="text-sm text-gray-700 dark:text-gray-300 py-2">
                <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{matchingCount.toLocaleString()}</span> <br/> contacts
                match this segment
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-white py-2">Add a rule to see how many contacts match.</p>
            )}
          </div>

          {/* Saved segment's real contacts — GET /segments/{id}, only ever
              reflects the last-saved rule_tree (populate on open / after Save). */}
          {contactRows.length > 0 && (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-white mb-3">Contacts in this segment</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-800">
                      <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 dark:text-white">Contact Name</th>
                      <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 dark:text-white">Phone</th>
                      <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 dark:text-white">Email</th>
                      <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 dark:text-white">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactRows.map((c, i) => (
                      <tr key={i} className="border-b border-gray-50 dark:border-gray-800/60">
                        <td className="py-2 px-2 text-gray-800 dark:text-gray-200">
                          {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
                        </td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400">{maskPhone(c.phone)}</td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400">{maskEmail(c.email)}</td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400">{(c.sources || []).join(", ") || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ConvoAI panel — a persistent full-height sidebar, not a content-hugging box. */}
        {convoAiOpen ? (
          <div className="w-full xl:w-[300px] shrink-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col xl:h-[calc(100vh-3rem)] xl:sticky xl:top-6">
            <div className="flex items-center justify-between mb-4 bg-emerald-50 dark:bg-emerald-500/10 -mx-4 -mt-4 px-4 py-3 rounded-t-xl">
              <div className="flex items-center gap-1.5 font-bold text-sm text-gray-900 dark:text-gray-100">
                <ConvoAiIcon size={16} /> ConvoAI
              </div>
              <button onClick={() => setConvoAiOpen(false)} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 bg-transparent border-none cursor-pointer">
                <X size={16} />
              </button>
            </div>

            <div className="pt-1">
              <p className="text-[16px] text-[#3A3428] dark:text-gray-200 mb-1">Total Contacts</p>
              <p className="text-[20px] flex  text-[#3A3428] dark:text-gray-100 font-semibold">
                <UserGroupIcon />
                {countLoading ? "…" : matchingCount !== null ? matchingCount.toLocaleString() : baselineTotal !== null ? baselineTotal.toLocaleString() : "—"}
              </p>
              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">
                {countError
                  ? "Couldn't calculate matching contacts right now."
                  : "This is the number of contacts that match your current segment rules."}
              </p>

              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-4 mb-1.5">Contacts in your segment</p>
              <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      baselineTotal
                        ? Math.min(100, Math.round(((matchingCount ?? baselineTotal) / baselineTotal) * 100))
                        : 0
                    }%`,
                  }}
                />
              </div>
              <p className="text-xs font-bold text-gray-900 dark:text-gray-100 text-right mt-1">
                {(matchingCount ?? baselineTotal) !== null ? `${(matchingCount ?? baselineTotal)!.toLocaleString()} contacts` : "—"}
              </p>
            </div>

            <div className="flex-1" />

            <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-2">Ask ConvoAI</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {["Optimise Segment", "Filter High-Value Segment"].map((chip) => (
                  <button
                    key={chip}
                    onClick={() => setConvoPrompt(chip)}
                    className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/30 hover:bg-indigo-100 dark:hover:bg-indigo-500/25 transition-colors"
                  >
                    {chip}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5">
                <input
                  value={convoPrompt}
                  onChange={(e) => setConvoPrompt(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAskConvoAi()}
                  placeholder="Ask anything..."
                  className="flex-1 text-xs outline-none border-none bg-transparent min-w-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
                <Mic size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" />
                <Paperclip size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" />
                <button
                  onClick={handleAskConvoAi}
                  disabled={!convoPrompt.trim()}
                  className="w-6 h-6 flex items-center justify-center rounded-md bg-indigo-600 text-white border-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  <Send size={11} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setConvoAiOpen(true)}
            className="fixed bottom-6 right-6 w-11 h-11 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center shadow-lg border-none cursor-pointer"
          >
            <ConvoAiIcon size={18} color="#ffffff" />
          </button>
        )}
      </div>
    </div>
  );
};

// ── Small sub-components ──────────────────────────────────────────────

const ScopeCard: React.FC<{ selected: boolean; title: string; description: string; onClick: () => void }> = ({
  selected,
  title,
  description,
  onClick,
}) => (
  <button
    onClick={onClick}
    className={`text-left border rounded-xl p-4 transition-colors ${
      selected
        ? "border-emerald-300 dark:border-emerald-500/50 bg-emerald-50/40 dark:bg-emerald-500/10"
        : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
    }`}
  >
    <div className="flex items-center gap-2 mb-1">
      <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${selected ? "bg-emerald-500" : "border border-gray-300 dark:border-gray-600"}`}>
        {selected && <Check size={11} className="text-white" />}
      </span>
      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
    </div>
    <p className="text-xs text-gray-500 dark:text-white ml-6">{description}</p>
  </button>
);

const COUNT_OPERATORS: { value: NonNullable<EventRule["countOperator"]>; label: string }[] = [
  { value: "", label: "at least once, ever" },
  { value: "equals", label: "exactly" },
  { value: "not_equals", label: "not equal to" },
  { value: "greater_than", label: "more than" },
  { value: "less_than", label: "fewer than" },
];

const FieldLoader: React.FC = () => (
  <div className="w-3.5 h-3.5 shrink-0 animate-spin rounded-full border-2 border-indigo-200 dark:border-indigo-500/30 border-t-indigo-600 dark:border-t-indigo-400" title="Recalculating matches…" />
);

const RuleRow: React.FC<{
  rule: SegmentRule;
  showLoader?: boolean;
  isPickerOpen: boolean;
  onOpenPicker: () => void;
  onClosePicker: () => void;
  attributeCatalog: AttributeCatalog;
  attributesLoading: boolean;
  events: EventCatalog;
  eventsLoading: boolean;
  onSelectAttribute: (field: AttributeCatalogField) => void;
  onSelectEvent: (event: EventCatalogItem) => void;
  onChangeOperator: (operator: RuleOperator) => void;
  onChangeValue: (value: string) => void;
  onChangeValueTo: (value: string) => void;
  onChangeOccurredInDays: (days: number | undefined) => void;
  onChangeCountOperator: (op: EventRule["countOperator"]) => void;
  onChangeCountValue: (value: string) => void;
  channelOptions: string[];
  onChangeChannelFilter: (channel: string) => void;
  onRemove: () => void;
}> = ({
  rule,
  showLoader,
  isPickerOpen,
  onOpenPicker,
  onClosePicker,
  attributeCatalog,
  attributesLoading,
  events,
  eventsLoading,
  onSelectAttribute,
  onSelectEvent,
  onChangeOperator,
  onChangeValue,
  onChangeValueTo,
  onChangeOccurredInDays,
  onChangeCountOperator,
  onChangeCountValue,
  channelOptions,
  onChangeChannelFilter,
  onRemove,
}) => {
  const fieldLabel = rule.kind === "event" ? rule.eventType.replace(/_/g, " ") : rule.label || rule.field;
  const picker = isPickerOpen && (
    <AttributePicker
      attributeCatalog={attributeCatalog}
      attributesLoading={attributesLoading}
      events={events}
      eventsLoading={eventsLoading}
      onSelectAttribute={onSelectAttribute}
      onSelectEvent={onSelectEvent}
      onClose={onClosePicker}
    />
  );

  if (rule.kind === "event") {
    return (
      <div className="flex flex-col gap-2 mb-2 last:mb-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <button
              onClick={onOpenPicker}
              className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors min-w-[130px] justify-between"
            >
              {fieldLabel || "Select event"} <ChevronDown size={13} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
            </button>
            {picker}
          </div>
          <span className="text-sm text-gray-600 dark:text-gray-400">occurred within</span>
          <input
            type="number"
            min={1}
            placeholder="any time"
            value={rule.occurredInDays ?? ""}
            onChange={(e) => onChangeOccurredInDays(e.target.value ? Math.max(1, parseInt(e.target.value, 10)) : undefined)}
            className="w-20 text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-2 bg-white dark:bg-gray-900"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">days</span>
          <div className="flex items-center gap-2 ml-auto">
            {showLoader && <FieldLoader />}
            <button onClick={onRemove} className="p-1.5 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 bg-transparent border-none cursor-pointer shrink-0">
              <X size={15} />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap pl-1">
          <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Happened</span>
          <select
            value={rule.countOperator || ""}
            onChange={(e) => onChangeCountOperator((e.target.value || "") as EventRule["countOperator"])}
            className="text-xs text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900"
          >
            {COUNT_OPERATORS.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
          {rule.countOperator && (
            <input
              type="number"
              min={0}
              value={rule.countValue ?? ""}
              onChange={(e) => onChangeCountValue(e.target.value)}
              placeholder="times"
              className="w-16 text-xs text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900"
            />
          )}
        </div>
        {isBroadcastEventType(rule.eventType, events) && (
          <div className="flex items-center gap-2 flex-wrap pl-1">
            <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Channel</span>
            <select
              value={rule.attributePath === "broadcast_channel" ? rule.eventValue || "" : ""}
              onChange={(e) => onChangeChannelFilter(e.target.value)}
              className="text-xs text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900 capitalize"
            >
              <option value="">Any channel</option>
              {channelOptions.map((c) => (
                <option key={c} value={c} className="capitalize">
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    );
  }

  const operatorOptions = rule.operators && rule.operators.length > 0 ? rule.operators : ALL_OPERATORS.map((o) => o.value);
  const isValueless = !!rule.operator && VALUELESS_OPERATORS.includes(rule.operator);
  const isBetween = rule.operator === "between";
  const inputType = rule.type === "number" ? "number" : rule.type === "date" ? "date" : "text";

  const renderValueInput = (value: string, onChange: (v: string) => void, placeholder: string) =>
    rule.type === "boolean" ? (
      <select value={value} onChange={(e) => onChange(e.target.value)} className="text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-900">
        <option value="">Select…</option>
        <option value="true">True</option>
        <option value="false">False</option>
      </select>
    ) : rule.type === "select" ? (
      <select value={value} onChange={(e) => onChange(e.target.value)} className="text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-900">
        <option value="">Select…</option>
        {(rule.options || []).map((opt) => {
          const isLogoOption = typeof opt === "object";
          const optValue = isLogoOption ? (opt as AttributeSelectOption).value : opt;
          return (
            <option key={optValue} value={optValue}>
              {optValue}
            </option>
          );
        })}
      </select>
    ) : (
      <input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className=" text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-900"
      />
    );

  return (
    <div className="flex items-center gap-2 mb-2 last:mb-0 flex-wrap">
      <div className="relative">
        <button
          onClick={onOpenPicker}
          className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors min-w-[130px] justify-between"
        >
          {fieldLabel || "Select field"} <ChevronDown size={13} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
        </button>
        {picker}
      </div>

      <select
        value={rule.operator}
        onChange={(e) => onChangeOperator(e.target.value as RuleOperator)}
        disabled={!rule.field}
        className="text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 disabled:bg-gray-50 dark:disabled:bg-gray-800"
      >
        <option value="">operator</option>
        {operatorOptions.map((op) => (
          <option key={op} value={op}>
            {OPERATOR_LABELS[op]}
          </option>
        ))}
      </select>

      {!isValueless && rule.operator && (
        <>
          {renderValueInput(rule.value, onChangeValue, isBetween ? "from" : `Enter ${rule.label || rule.field}`)}
          {isBetween && renderValueInput(rule.valueTo || "", onChangeValueTo, "to")}
        </>
      )}

      {showLoader && <FieldLoader />}

      <button onClick={onRemove} className="p-1.5 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 bg-transparent border-none cursor-pointer shrink-0">
        <X size={15} />
      </button>
    </div>
  );
};

export default SegmentBuilder;
